import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil, finalize } from 'rxjs';
import { ICerere } from '../../cereri.interface';
import { CereriService } from '../../cereri.service';

interface ICitizenAppointment {
    id: number;
    created_at: string;
    status_name: string;
    status_slug: string;
    status_bg_color: string;
    status_color: string;
    validated_date: string | null;
    cancel_date: string | null;
    presence_status: number | null;
    source: number;
    cetatean_name: string;
    cetatean_cnp: string;
    cetatean_telefon: string;
    cetatean_email: string;
    slot_date: string | null;
    slot_time_from: string | null;
    slot_time_end: string | null;
    slot_locatie: string | null;
    slot_serviciu: string | null;
    slot_modul: string | null;
}

@Component({
    selector: 'app-istoric-programari',
    templateUrl: './istoric-programari.component.html',
    styleUrls: ['./istoric-programari.component.scss'],
})
export class IstoricProgramariComponent implements OnInit, OnDestroy {
    @Input() cerere!: ICerere;

    private destroy$ = new Subject<void>();

    appointments: ICitizenAppointment[] = [];
    pastAppointments: ICitizenAppointment[] = [];
    futureAppointments: ICitizenAppointment[] = [];
    isLoading = false;
    isCollapsed = false;
    matchInfo: { cnp: boolean; telefon: boolean; email: boolean } = {
        cnp: false,
        telefon: false,
        email: false,
    };
    total = 0;

    // Paginare FE
    pageSize = 5;
    futurePage = 0;
    pastPage = 0;

    constructor(
        private cereriService: CereriService,
    ) {}

    ngOnInit() {
        this.loadHistory();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadHistory() {
        if (!this.cerere?.id) return;

        this.isLoading = true;
        this.cereriService.getCitizenHistory(this.cerere.id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false),
            )
            .subscribe({
                next: (res: any) => {
                    const data = res?.data || {};
                    this.appointments = data.appointments || [];
                    this.matchInfo = data.match_info || { cnp: false, telefon: false, email: false };
                    this.total = data.total || 0;

                    this.splitAppointments();
                },
                error: () => {
                    this.appointments = [];
                    this.total = 0;
                },
            });
    }

    splitAppointments() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.futureAppointments = [];
        this.pastAppointments = [];

        for (const apt of this.appointments) {
            if (apt.slot_date) {
                const slotDate = new Date(apt.slot_date);
                slotDate.setHours(0, 0, 0, 0);
                if (slotDate >= today) {
                    this.futureAppointments.push(apt);
                } else {
                    this.pastAppointments.push(apt);
                }
            } else {
                this.pastAppointments.push(apt);
            }
        }

        // Reset paginare
        this.futurePage = 0;
        this.pastPage = 0;
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    openAppointment(appointmentId: number) {
        window.open('/#/ghiseul-unic-cereri?openCerere=' + appointmentId, '_blank');
    }

    getSourceLabel(source: number): string {
        return source === 5 ? 'Operator' : 'Portal';
    }

    getPresenceLabel(status: number | null): string {
        if (status === 1) return 'Prezent';
        if (status === 2) return 'Absent';
        return '';
    }

    formatSlotDate(dateStr: string | null): string {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
        return dateStr;
    }

    getCardBorderClass(apt: ICitizenAppointment): string {
        if (apt.cancel_date) return 'border-cancelled';
        if (apt.slot_date) {
            const slotDate = new Date(apt.slot_date);
            slotDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (slotDate >= today) return 'border-future';
        }
        if (apt.presence_status === 1) return 'border-present';
        if (apt.presence_status === 2) return 'border-absent';
        return 'border-past';
    }

    // Paginare - Viitoare
    get pagedFuture(): ICitizenAppointment[] {
        const start = this.futurePage * this.pageSize;
        return this.futureAppointments.slice(start, start + this.pageSize);
    }

    get futureTotalPages(): number {
        return Math.ceil(this.futureAppointments.length / this.pageSize);
    }

    prevFuturePage() {
        if (this.futurePage > 0) this.futurePage--;
    }

    nextFuturePage() {
        if (this.futurePage < this.futureTotalPages - 1) this.futurePage++;
    }

    // Paginare - Trecute
    get pagedPast(): ICitizenAppointment[] {
        const start = this.pastPage * this.pageSize;
        return this.pastAppointments.slice(start, start + this.pageSize);
    }

    get pastTotalPages(): number {
        return Math.ceil(this.pastAppointments.length / this.pageSize);
    }

    prevPastPage() {
        if (this.pastPage > 0) this.pastPage--;
    }

    nextPastPage() {
        if (this.pastPage < this.pastTotalPages - 1) this.pastPage++;
    }
}
